const STORAGE_KEY = "samurai:lastError";

/**
 * Where the app remembers what actually went wrong.
 *
 * The error boundary used to be `componentDidCatch()` — declared with no
 * parameters, so React handed it the error and the component stack and both
 * were dropped on the floor. It then redirected to /error/500, whose only
 * content is the sentence "We experience problems at the moment". Nothing was
 * logged, so the browser console was empty too: a whole page down, tenant-wide,
 * with literally zero information anywhere for the person looking at it.
 *
 * sessionStorage rather than component state or redux because the boundary
 * navigates to another route to report, which unmounts whatever held it, and
 * because it has to survive the reload people reflexively do next.
 */
export const recordError = (error, extra = {}) => {
  // Always in the console, whatever else happens below.
  console.error("[samurai] captured error", error, extra);

  try {
    const record = {
      at: new Date().toISOString(),
      url: window.location.href,
      name: (error && error.name) || "Error",
      message: (error && error.message) || String(error),
      stack: (error && error.stack) || null,
      ...extra,
    };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    return record;
  } catch (e) {
    // sessionStorage can be unavailable (Safari private mode, quota). The
    // console.error above already happened; losing the detail page is not a
    // reason to throw a second error out of an error handler.
    return null;
  }
};

export const readLastError = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearLastError = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* nothing to clear */
  }
};

/**
 * Pulls the real reason out of an axios failure. The v2 API answers 5xx with
 * `{statusCode, error, message, path, requestId}`; v1 answers with
 * `{errors: {error: "..."}}`. Anything else falls back to axios' own message.
 */
export const describeRequestError = (error) => {
  const response = error && error.response;
  const data = response && response.data;

  const message =
    (data && (data.message || (data.errors && data.errors.error))) ||
    (error && error.message) ||
    "Request failed";

  return {
    name: (data && data.error) || (error && error.name) || "RequestError",
    message: typeof message === "string" ? message : JSON.stringify(message),
    status: response ? response.status : null,
    path: (data && data.path) || (error && error.config && error.config.url),
    requestId: data && data.requestId,
  };
};
